export type Heading = {
    id: string
    text: string
}

const decode = (value: string): string => {
    return value
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;|&rsquo;|&lsquo;/g, "'")
        .replace(/&[a-z]+;/g, '')
        .trim()
}

export const slugify = (value: string): string => {
    return value
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64)
}

/**
 * Gives every h2 in a rich text value a stable id and returns the list,
 * so article pages can render a table of contents with working anchors.
 * Runs on the server only; the stored CMS value is never modified.
 */
export const withHeadingIds = (html: string): { html: string, headings: Array<Heading> } => {
    const headings: Array<Heading> = []
    const used = new Set<string>()

    const output = html.replace(/<h2([^>]*)>([\s\S]*?)<\/h2>/gi, (match, attributes: string, inner: string) => {
        const text = decode(inner)

        if (!text) return match

        const base = slugify(text) || 'section'
        let id = base
        let suffix = 2

        while (used.has(id)) id = `${base}-${suffix++}`

        used.add(id)
        headings.push({ id, text })

        const cleaned = attributes.replace(/\s+id=("[^"]*"|'[^']*')/i, '')

        return `<h2${cleaned} id="${id}">${inner}</h2>`
    })

    return { html: output, headings }
}

export const plainText = (html: string): string => decode(html.replace(/<\/(p|li|h\d)>/gi, ' '))
